/**
 * Orders Service
 * Handles order-related API calls
 */

import { apiClient } from "../client";
import type {
  Order,
  OrderListResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  FilterParams,
} from "../types";

export const ordersService = {
  /**
   * Get list of orders
   * Endpoint: DirectorModule/orders
   */
  async getOrders(params?: FilterParams): Promise<OrderListResponse> {
    const response = await apiClient.get<any>("DirectorModule/orders", params);
    const data = response.data;
    
    // Handle both array response and wrapped response
    if (Array.isArray(data)) {
      return {
        orders: data as any[],
        total: data.length,
        page: 1,
        limit: data.length,
        totalPages: 1,
      };
    }
    
    return data as OrderListResponse;
  },

  /**
   * Get single order by ID
   */
  async getOrderById(orderId: string): Promise<Order> {
    const response = await apiClient.get<Order>(`/orders/${orderId}`);
    return response.data;
  },

  /**
   * Create new order
   */
  async createOrder(data: CreateOrderRequest): Promise<CreateOrderResponse> {
    const response = await apiClient.post<CreateOrderResponse>("/orders", data);
    return response.data;
  },

  /**
   * Update order
   */
  async updateOrder(orderId: string, data: Partial<Order>): Promise<Order> {
    const response = await apiClient.put<Order>(`/orders/${orderId}`, data);
    return response.data;
  },

  /**
   * Delete order
   */
  async deleteOrder(orderId: string): Promise<void> {
    await apiClient.delete(`/orders/${orderId}`);
  },

  /**
   * Update order status
   */
  async updateOrderStatus(
    orderId: string,
    status: "pending" | "confirmed" | "delivered" | "cancelled"
  ): Promise<Order> {
    const response = await apiClient.patch<Order>(`/orders/${orderId}/status`, { status });
    return response.data;
  },

  /**
   * Get order details
   * Endpoint: Order/details
   */
  async getOrderDetails(params: {
    firmId: number;
    orderNo: number;
    partyCode?: string;
    salesManCode?: string;
  }): Promise<any[]> {
    const response = await apiClient.get<any[]>("Order/details", params);
    return response.data;
  },
};

